/**
 * Token-processing lifecycle hooks for consumers that want to observe progress
 * or control UI-only mutation timing. The shared token-processing core must not
 * depend on browser streaming state; backend consumers use the immediate
 * lifecycle, while UI consumers adapt their state manager behind this contract.
 */
const tokenProcessingLifecycleBrand: unique symbol = Symbol('TokenProcessingLifecycle');

export interface TokenProcessingLifecycleHooks {
  setEntryIdForProcessing(entryId: string): void;
  clearEntryIdForProcessing(): void;
  setFieldIdForProcessing(fieldId: string): void;
  clearFieldIdForProcessing(): void;
  markValueProcessed(fieldId: string, entryId: string): void;
  markFieldSeen(fieldId: string): void;
  resetSingleFieldStreaming(): void;
  markFieldsComplete(): void;
  markFirstLayoutProcessed(): void;
  setEntryOperation(operation: 'edit' | 'delete' | undefined): void;
  setFieldOperation(operation: 'delete' | undefined): void;
  scheduleFieldDeletion(fieldId: string, deleteCallback: () => void): void;
  scheduleEntryDeletion(entryId: string, deleteCallback: () => void): void;
  resetOnStreamingEnd(): void;
}

export interface TokenProcessingLifecycle extends TokenProcessingLifecycleHooks {
  readonly [tokenProcessingLifecycleBrand]: true;
}

export function createTokenProcessingLifecycle(
  hooks: TokenProcessingLifecycleHooks
): TokenProcessingLifecycle {
  return {
    [tokenProcessingLifecycleBrand]: true,
    setEntryIdForProcessing: (entryId) => hooks.setEntryIdForProcessing(entryId),
    clearEntryIdForProcessing: () => hooks.clearEntryIdForProcessing(),
    setFieldIdForProcessing: (fieldId) => hooks.setFieldIdForProcessing(fieldId),
    clearFieldIdForProcessing: () => hooks.clearFieldIdForProcessing(),
    markValueProcessed: (fieldId, entryId) => hooks.markValueProcessed(fieldId, entryId),
    markFieldSeen: (fieldId) => hooks.markFieldSeen(fieldId),
    resetSingleFieldStreaming: () => hooks.resetSingleFieldStreaming(),
    markFieldsComplete: () => hooks.markFieldsComplete(),
    markFirstLayoutProcessed: () => hooks.markFirstLayoutProcessed(),
    setEntryOperation: (operation) => hooks.setEntryOperation(operation),
    setFieldOperation: (operation) => hooks.setFieldOperation(operation),
    scheduleFieldDeletion: (fieldId, deleteCallback) =>
      hooks.scheduleFieldDeletion(fieldId, deleteCallback),
    scheduleEntryDeletion: (entryId, deleteCallback) =>
      hooks.scheduleEntryDeletion(entryId, deleteCallback),
    resetOnStreamingEnd: () => hooks.resetOnStreamingEnd(),
  };
}

export function createImmediateTokenProcessingLifecycle(): TokenProcessingLifecycle {
  return createTokenProcessingLifecycle({
    setEntryIdForProcessing() {},
    clearEntryIdForProcessing() {},
    setFieldIdForProcessing() {},
    clearFieldIdForProcessing() {},
    markValueProcessed() {},
    markFieldSeen() {},
    resetSingleFieldStreaming() {},
    markFieldsComplete() {},
    markFirstLayoutProcessed() {},
    setEntryOperation() {},
    setFieldOperation() {},
    scheduleFieldDeletion(_fieldId, deleteCallback) {
      deleteCallback();
    },
    scheduleEntryDeletion(_entryId, deleteCallback) {
      deleteCallback();
    },
    resetOnStreamingEnd() {},
  });
}
